import { API_DOMAIN } from "@monorepo/constants";
import {
  IDetailedHistoryEntry,
  IFilteredHistoryEntry,
  IHistoryEntry,
  IServiceHistoryEntry,
  ITransactionEntry,
} from "@monorepo/types";
import { createApi } from "@reduxjs/toolkit/query/react";

import { customBaseQuery } from "../../helpers/baseQuery";

const transformPaidAtProperty = (value: any) => {
  if (typeof value === "string" && value.match(/(\d{4})-(\d{2})-(\d{2})\s(\d{2}):(\d{2})/gi)) {
    return value.split(" ").join("T");
  }
  return value;
};

const transformEntries = <T extends { paid_at?: string }>(entries: Array<T>): Array<T> =>
  entries?.map((e) => ({ ...e, paid_at: transformPaidAtProperty(e.paid_at) }));

export const historyApi = createApi({
  reducerPath: "historyApi",
  baseQuery: customBaseQuery<
    | Array<IHistoryEntry>
    | Array<IServiceHistoryEntry>
    | Array<ITransactionEntry>
    | Array<IFilteredHistoryEntry>
    | IDetailedHistoryEntry
    | ArrayBuffer
  >({
    baseUrl: API_DOMAIN,
  }),
  tagTypes: ["Entries", "DetailedEntries"],
  endpoints: (builder) => ({
    getHistoryByPeriod: builder.query<Array<IHistoryEntry>, number>({
      query: (period) => ({
        url: `/api/v1/history/year/period/${period}`,
      }),
      providesTags: ["Entries"],
      transformResponse: (response) => transformEntries(response as Array<IHistoryEntry>),
    }),
    getHistoryByOrderId: builder.query<IDetailedHistoryEntry, number>({
      query: (orderId) => ({
        url: `/api/v1/history/${orderId}`,
      }),
      providesTags: ["DetailedEntries"],
      transformResponse: (response: IDetailedHistoryEntry) => ({
        ...response,
        paid_at: transformPaidAtProperty(response.paid_at),
      }),
    }),
    getHistory: builder.query<Array<IFilteredHistoryEntry>, { cart_group_id: number; from?: string; till?: string }>({
      query: (params) => ({
        url: "/api/v1/history",
        params,
      }),
      providesTags: ["Entries"],
      transformResponse: (response) => transformEntries(response as Array<IFilteredHistoryEntry>),
    }),
    getHistoryBySearch: builder.query<Array<IHistoryEntry>, void>({
      query: () => ({
        url: "/api/v1/history/search-data",
      }),
      providesTags: ["Entries"],
      transformResponse: (response) => transformEntries(response as Array<IHistoryEntry>),
    }),
    getHistoryByServiceId: builder.query<
      IServiceHistoryEntry,
      {
        service_id: number;
        cart_group_id: number;
        from?: string;
        till?: string;
        customer_number?: Array<string | number>;
      }
    >({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      query: ({ service_id, ...params }) => {
        return {
          url: `/api/v1/history/service/${service_id}`,
          params,
        };
      },
      providesTags: ["DetailedEntries"],
    }),
    getTransactionsSummaryByMonth: builder.query<Array<ITransactionEntry>, { year: string; month: string }>({
      query: ({ year, month }) => ({
        url: `/api/v1/history/transactions/year/${year}/month/${month}`,
      }),
      providesTags: ["Entries"],
    }),
    getTransactionReportById: builder.query<ArrayBuffer, string>({
      query: (id) => ({
        url: `/api/v1/history/report/order/${id}`,
      }),
      extraOptions: {
        serializeResponse: false,
        responseToBuffer: true,
      },
    }),
    getHistoryReportById: builder.query<ArrayBuffer, number>({
      query: (id) => ({
        url: `/api/v1/history/report/${id}`,
      }),
      extraOptions: {
        serializeResponse: false,
        responseToBuffer: true,
      },
    }),
  }),
});

export const {
  useGetHistoryByPeriodQuery,
  useGetHistoryByOrderIdQuery,
  useGetHistoryByServiceIdQuery,
  useGetHistoryQuery,
  useLazyGetHistoryQuery,
  useGetHistoryBySearchQuery,
  useGetTransactionsSummaryByMonthQuery,
  useGetHistoryReportByIdQuery,
  useLazyGetHistoryReportByIdQuery,
} = historyApi;

export default historyApi.reducer;
